import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Check, Trash2, Lock } from "lucide-react";
import { S, MANROPE, FOCUS, API } from "./lib";

const KINDS = [["consent", "A consent not yet given"], ["landing_page", "A landing page not yet live"],
  ["print", "A print deadline"], ["legal", "Legal or client sign-off"], ["other", "Something else"]];

const TONE = {
  open: { background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" },
  chasing: { background: "#FFF8D6", border: "1px solid #E8D48A", color: "#8A6D00" },
  cleared: { background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" },
};

/* Dependencies in the way of a post. Claude can read these and move one to
   "chasing" with a note; clearing one is a person's call, so that button only
   exists here. */
export default function SocialGates({ post, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("consent");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const gates = post.gates || [];

  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg); onChanged?.(r.data.post); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  const add = () => {
    if (!text.trim()) { toast.info("Say what is in the way"); return; }
    run(() => axios.post(`${API}/posts/${post.id}/gates`,
      { kind, description: text.trim() }), "Dependency added").then(() => { setText(""); setAdding(false); });
  };

  return (
    <div className="rounded-[10px] p-3.5 space-y-3" data-testid="social-gates"
      style={{ background: S.card, border: `1px solid ${S.border}` }}>
      <div className="flex items-center gap-2">
        <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>Dependencies</div>
        <span className="text-[11.5px]" style={{ color: S.ink3 }}>
          {gates.length === 0 ? "nothing in the way" : `${gates.filter((g) => g.status !== "cleared").length} still open`}
        </span>
        <button onClick={() => setAdding(!adding)} data-testid="social-gate-add-toggle"
          className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase ${FOCUS}`}
          style={{ color: S.accentInk, letterSpacing: "0.04em" }}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>

      {adding && (
        <div className="rounded-[8px] p-2.5 space-y-2" style={{ background: S.panel, border: `1px solid ${S.divider}` }}>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="social-gate-kind"
            className={`w-full h-9 rounded-[7px] px-2 text-[12.5px] bg-white ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, color: S.ink }}>
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="social-gate-description"
            placeholder="e.g. Waiting on Boom Logistics to clear the photo"
            className={`w-full h-9 rounded-[7px] px-2.5 text-[12.5px] bg-white ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, color: S.ink }} />
          <button onClick={add} disabled={busy} data-testid="social-gate-save"
            className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
            style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000" }}>
            Add dependency
          </button>
        </div>
      )}

      {gates.map((g) => (
        <div key={g.id} className="rounded-[8px] px-2.5 py-2 text-[12.5px]"
          data-testid={`social-gate-${g.id}`} style={TONE[g.status] || TONE.open}>
          <div className="flex items-start gap-2">
            <span className="flex-1">
              <b className="uppercase text-[10px] tracking-[0.08em]">
                {(g.status || "open").replace("_", " ")}
              </b>
              <span className="block">{g.description}</span>
              {g.note && <span className="block text-[11.5px] opacity-80">{g.note}</span>}
              {g.last_progressed_by && (
                <span className="block text-[11px] opacity-70">
                  Last chased by {g.last_progressed_by}
                </span>
              )}
            </span>
            {g.status !== "cleared" ? (
              <button onClick={() => run(() => axios.put(`${API}/posts/${post.id}/gates/${g.id}`,
                { description: g.description, status: "cleared" }), "Dependency cleared")}
                disabled={busy} data-testid={`social-gate-clear-${g.id}`} title="Only a person can clear this"
                className={`h-7 px-2 rounded-[6px] text-[10.5px] font-bold uppercase shrink-0 ${FOCUS}`}
                style={{ background: "#fff", border: `1px solid ${S.border}`, color: S.good }}>
                <Check className="w-3 h-3 inline mr-0.5" /> Clear
              </button>
            ) : <Lock className="w-3.5 h-3.5 shrink-0 opacity-50" />}
            <button onClick={() => run(() => axios.delete(`${API}/posts/${post.id}/gates/${g.id}`),
              "Dependency removed")} disabled={busy} data-testid={`social-gate-remove-${g.id}`}
              className={`h-7 w-7 rounded-[6px] flex items-center justify-center shrink-0 ${FOCUS}`}
              style={{ background: "#fff", border: `1px solid ${S.border}`, color: "#8E1212" }}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
      {(post.consent_note || "").trim() && !post.consent_cleared && (
        <div className="rounded-[8px] px-2.5 py-2 text-[12.5px]" style={TONE.open}
          data-testid="social-gate-consent">
          <b className="uppercase text-[10px] tracking-[0.08em]">Consent</b>
          <span className="block">{post.consent_note}</span>
          <span className="block text-[11px] opacity-70">
            Tick "Consent cleared" below once it is given.
          </span>
        </div>
      )}
    </div>
  );
}
